import { useState, useEffect, useRef, createContext, useContext, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { Toaster } from "@/components/ui/sonner";

// Pages
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import DashboardPage from "@/pages/DashboardPage";
import TransactionDetailPage from "@/pages/TransactionDetailPage";

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Auth Provider — uses onAuthStateChange as sole source of truth
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);

  const fetchProfile = useCallback(async (userId) => {
    console.log('[Auth] fetchProfile start, userId:', userId);
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      console.log('[Auth] fetchProfile result:', {
        hasData: !!data,
        name: data?.full_name,
        role: data?.role,
        error: error?.message || null
      });

      if (data && mountedRef.current) {
        setProfile(data);
      }
    } catch (err) {
      console.error('[Auth] fetchProfile threw:', err);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;

    // onAuthStateChange is the SOLE source of truth.
    // INITIAL_SESSION fires once on startup with the stored session (or null).
    // SIGNED_IN fires after login.
    // SIGNED_OUT fires after logout.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('[Auth] onAuthStateChange:', event, 'email:', session?.user?.email || 'none', 'uid:', session?.user?.id || 'none');

      if (!mountedRef.current) return;

      if (event === 'INITIAL_SESSION') {
        // This is the FIRST event — always fires, even if session is null
        if (session?.user) {
          console.log('[Auth] Initial session found, setting user');
          setUser(session.user);
          fetchProfile(session.user.id);
        } else {
          console.log('[Auth] No initial session');
        }
        // ALWAYS clear loading after initial session resolves
        setLoading(false);
        console.log('[Auth] loading=false (initial session handled)');
      } else if (event === 'SIGNED_IN') {
        console.log('[Auth] User signed in');
        setUser(session.user);
        fetchProfile(session.user.id);
      } else if (event === 'SIGNED_OUT') {
        console.log('[Auth] User signed out');
        setUser(null);
        setProfile(null);
      } else if (event === 'TOKEN_REFRESHED') {
        console.log('[Auth] Token refreshed');
        if (session?.user) {
          setUser(session.user);
        }
      }
    });

    // Safety: if INITIAL_SESSION never fires, force loading=false after 5s
    const safety = setTimeout(() => {
      if (mountedRef.current) {
        setLoading(prev => {
          if (prev) {
            console.warn('[Auth] SAFETY: forcing loading=false at 5s (INITIAL_SESSION never fired)');
          }
          return false;
        });
      }
    }, 5000);

    return () => {
      mountedRef.current = false;
      clearTimeout(safety);
      subscription.unsubscribe();
    };
  }, [fetchProfile]);

  // login sets user SYNCHRONOUSLY before returning, so navigate() after login() sees the user
  const login = useCallback(async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    console.log('[Auth] login success, setting user synchronously:', data.user?.email);
    setUser(data.user);
    // Start profile fetch (non-blocking)
    fetchProfile(data.user.id);
    return data;
  }, [fetchProfile]);

  const register = useCallback(async (email, password, fullName, role) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { full_name: fullName, role } }
    });
    if (error) throw error;
    if (data.user) {
      console.log('[Auth] register success, setting user synchronously:', data.user?.email);
      setUser(data.user);
      fetchProfile(data.user.id);
    }
    return data;
  }, [fetchProfile]);

  const logout = useCallback(async () => {
    console.log('[Auth] Logout initiated');
    // Clear local state IMMEDIATELY
    setUser(null);
    setProfile(null);
    try {
      const { error } = await supabase.auth.signOut();
      if (error) console.error('[Auth] signOut error:', error);
      else console.log('[Auth] signOut complete');
    } catch (err) {
      console.error('[Auth] signOut threw:', err);
    }
  }, []);

  const updateProfile = useCallback(async (updates) => {
    if (!user) return;
    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', user.id)
      .select()
      .single();
    if (error) throw error;
    setProfile(data);
    return data;
  }, [user]);

  // Display values: profile (DB truth) > user_metadata (signup data) > email fallback
  const displayName = profile?.full_name || user?.user_metadata?.full_name || user?.email?.split('@')[0] || '';
  const displayEmail = profile?.email || user?.email || '';
  const displayRole = profile?.role || user?.user_metadata?.role || 'buyer';

  return (
    <AuthContext.Provider value={{
      user,
      profile,
      loading,
      login,
      register,
      logout,
      updateProfile,
      isAgent: displayRole === 'agent',
      displayName,
      displayEmail,
      displayRole,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// App Router
function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route
        path="/dashboard"
        element={<ProtectedRoute><DashboardPage /></ProtectedRoute>}
      />
      <Route
        path="/transaction/:transactionId"
        element={<ProtectedRoute><TransactionDetailPage /></ProtectedRoute>}
      />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
